import React, { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useUIContext } from '../../../../context/UIContext'
import DayJS from '../../../../helpers/DayJS'
import { useReports } from '../../Context/ReportsContext'

type SelectDates = {
    from: string
    to: string
}

export const FilterHeader = () => {
    const { t } = useTranslation('teamReports')
    const { toastNotification } = useUIContext()

    const { reportQuery, setReportQueryFn, activePeriod, setActivePeriod, setPredefinedPeriod } = useReports()
    const { from, to } = reportQuery

    const [selectDates, setSelectDates] = useState<SelectDates>({
        from: from || '',
        to: to || '',
    })

    // Update local state when reportQuery changes
    useEffect(() => {
        setSelectDates({
            from: from || '',
            to: to || '',
        })
    }, [from, to])

    const validateDates = () => {
        const fromDate = DayJS(selectDates.from)
        const toDate = DayJS(selectDates.to)

        const isValidDateRange = fromDate.isSameOrBefore(toDate) && toDate.isSameOrAfter(fromDate)
        if (isValidDateRange) {
            setReportQueryFn({ ...selectDates })
        } else {
            toastNotification(t('invalid date range'), 'error', 2)
        }
    }

    const setSelectDatesFn = (data: Partial<SelectDates>) => {
        setSelectDates({ ...selectDates, ...data })
    }

    const handleOnClick = () => {
        validateDates()
        // Switch to manual mode when applying custom dates
        setActivePeriod(-1)
    }

    // Handle manual date changes - switch to manual mode
    const handleDateChange = (field: keyof SelectDates, value: string) => {
        setSelectDatesFn({ [field]: value })
        // Switch to manual mode when manually changing dates
        if (activePeriod !== -1) {
            setActivePeriod(-1)
        }
    }

    // Calculate min date based on cohorts
    const minValidDate = useMemo(() => {
        if (!reportQuery.cohorts?.length) return undefined

        // Find the earliest cohort date
        const sortedCohorts = [...reportQuery.cohorts].sort((a, b) => {
            // Auto-detect format
            const isApiFormat = a.includes('-') && a.indexOf('-') === 4
            const cohortFormat = isApiFormat ? 'YYYY-MM-DD' : 'DD-MM-YYYY'

            const dateA = DayJS(a, cohortFormat)
            const dateB = DayJS(b, cohortFormat)
            return dateA.isBefore(dateB) ? -1 : 1
        })

        const isApiFormat = sortedCohorts[0]?.includes('-') && sortedCohorts[0]?.indexOf('-') === 4
        const cohortFormat = isApiFormat ? 'YYYY-MM-DD' : 'DD-MM-YYYY'

        return DayJS(sortedCohorts[0], cohortFormat).format('YYYY-MM-DD')
    }, [reportQuery.cohorts])

    const maxValidDate = DayJS().format('YYYY-MM-DD')

    return (
        <FilterHeaderStyles className="filter-dates">
            <BookmarkDateRanges period={activePeriod} setPredefinedPeriod={setPredefinedPeriod} />
            <div className="range">
                <label htmlFor="from" className="titles">
                    <span className="upper-title">{t('from')}</span>
                    <input
                        type="date"
                        name="from"
                        id="from"
                        min={minValidDate}
                        value={selectDates.from}
                        className="rangeDateSelector"
                        onChange={({ target }) => handleDateChange('from', target.value)}
                    />
                </label>
                <label htmlFor="to" className="titles">
                    <span className="upper-title">{t('to')}</span>
                    <input
                        type="date"
                        name="to"
                        id="to"
                        max={maxValidDate}
                        className="rangeDateSelector"
                        value={selectDates.to}
                        onChange={({ target }) => handleDateChange('to', target.value)}
                    />
                </label>
            </div>
            <button className="applyBtn" onClick={handleOnClick}>
                {t('apply')}
            </button>
        </FilterHeaderStyles>
    )
}

const BookmarkDateRanges = ({
    period,
    setPredefinedPeriod,
}: {
    period: number
    setPredefinedPeriod: (period: number) => void
}) => {
    const { t } = useTranslation('teamReports')

    const options = [
        {
            label: `${t('today')}`,
            period: 0,
        },
        {
            label: `${t('yesterday')}`,
            period: 1,
        },
        {
            label: `${t('last 7 days')}`,
            period: 2,
        },
    ]

    const handlePeriodClick = (periodIndex: number) => setPredefinedPeriod(periodIndex)

    return (
        <div className="titles">
            <span className="upper-title">{t('select period')}</span>
            <div className="selector">
                {options.map(({ label, period: periodIndex }) => {
                    return (
                        <button
                            className={`buttonDates ${periodIndex === period ? 'selected' : ''}`}
                            onClick={() => handlePeriodClick(periodIndex)}
                            key={label}
                        >
                            {label}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export const FilterHeaderStyles = styled.header`
    min-width: 400px;
    min-height: inherit;
    margin: 20px 0;
    padding: 2em;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2em;
    background-color: white;
    border-radius: var(--radius);

    .titles {
        width: 100%;
        display: grid;
        font-weight: bold;
        font-size: 10px;
    }

    .titles > input {
        font-size: 14px;
    }

    .titles > button {
        display: block;
        font-size: 14px;
    }

    .upper-title {
        text-transform: uppercase;
        margin-left: 10px;
    }

    .range {
        display: flex;
        justify-content: flex-end;
        align-items: center;
    }

    .rangeDateSelector {
        border: 1px solid #f2f2f2 !important;
        min-width: 130px;
        text-align: center;
        padding: 0px 10px 0px 10px;
        border-radius: 10px;
        min-height: 36px;
        height: 36px !important;
        margin: 8px 5px 5px;
        color: #073045;
        font-size: 14px;
        font-weight: 600;
    }

    .selector {
        display: inline-flex;
    }

    .buttonDates {
        width: 136px;
        border: 1px solid #f2f2f2;
        height: 36px;
        margin: 8px 0;
        font-size: 14px;
        font-weight: 700;
        color: #003750;
        background: #f8f8f8;
        cursor: pointer;
    }

    .buttonDates:first-of-type {
        border-radius: 8px 0px 0px 8px;
    }

    .buttonDates:last-of-type {
        border-radius: 0px 8px 8px 0px;
    }

    .buttonDates:only-child {
        border-radius: 8px;
    }

    .selected {
        background: #ffffff;
    }

    .buttonDates:hover {
        background: #e6e6e6;
    }

    .applyBtn {
        border-radius: 8px;
        height: 36px;
        background-color: white;
        border: 2px solid #003750;
        background: #ffffff 0% 0% no-repeat padding-box;
        color: #003750;
        font-weight: 900;
        font-size: 13px;
        min-width: 75px;
        cursor: pointer;
    }

    .applyBtn:hover {
        background-color: #eaeaea;
    }
`
